import { icon } from './templating/icon.element';
import { MenuOption } from './types/menu-option.type';
import { createElement } from './utils/create-element.fn';

export function renderMenuOptions<T extends MenuOption<any>>(
  menu: HTMLElement,
  items: T[]
) {
  const menuItems = items.map((item, idx) => {
    const menuItemElement: HTMLElement =
      (menu.children[idx] as HTMLElement) ?? createElement('li');

    menuItemElement.className = '';
    [...menuItemElement.children].forEach((c) => c.remove());
    menuItemElement.textContent = '';

    if (item.selectable === false) {
      menuItemElement.classList.add('unselectable');
    }

    if (item.meta === true) {
      menuItemElement.classList.add('meta');
    } else if (item.selected) {
      menuItemElement.classList.add('selected');
      menuItemElement.appendChild(icon('check'));
    } else if (item.icon) {
      menuItemElement.appendChild(icon(item.icon));
    }

    menuItemElement.appendChild(document.createTextNode(item.text));

    return {
      element: menuItemElement,
      isMeta: item.meta === true,
      item,
    };
  });

  [...menu.children].forEach((c) => c.remove());
  menuItems.map(({ element }) => element).forEach((c) => menu.appendChild(c));

  return menuItems;
}

export function menu<T extends MenuOption<any>>(
  items: () => T[],
  options: {
    shouldNotDismissOnSelection?: boolean;
    backgroundIsNotDismissable?: boolean;
    onBackgroundDismissed?: () => void;
  } = { shouldNotDismissOnSelection: false, backgroundIsNotDismissable: false }
) {
  return new Promise<T>((resolve) => {
    const container = createElement('div', 'menu hidden');
    const menu = createElement('ul', 'limit');

    container.appendChild(menu);

    let menuItems = renderMenuOptions(menu, items());

    const containerClickListener = (e: MouseEvent) => {
      if (e.target === container) {
        if (typeof options?.onBackgroundDismissed === 'function') {
          options.onBackgroundDismissed();
        }

        destroy();
      }
    };
    const escKeyPressListener = (e: KeyboardEvent) => {
      if (
        e.key === 'Escape' ||
        e.code === 'Escape' ||
        e.which === 27 ||
        e.keyCode === 27
      ) {
        if (typeof options?.onBackgroundDismissed === 'function') {
          options.onBackgroundDismissed();
        }

        destroy();
      }
    };
    const destroy = () => {
      if (!options.backgroundIsNotDismissable) {
        container.removeEventListener('click', containerClickListener);
        document.removeEventListener('keydown', escKeyPressListener);
      }

      menuItemClickHandlerDestroys.forEach((d) => d());
      container.classList.add('hidden');
      setTimeout(() => container.remove(), 150);
    };

    document.body.appendChild(container);
    setTimeout(() => container.classList.remove('hidden'), 150);

    const menuItemClickHandlerDestroys = menuItems
      .filter(({ isMeta }) => !isMeta)
      .map(({ element, item }) => {
        const handler = async () => {
          if (
            !options.shouldNotDismissOnSelection ||
            item.overrideNonDismissRestriction === true
          ) {
            destroy();
          }

          if (
            item.callback !== undefined &&
            typeof item.callback === 'function'
          ) {
            await item.callback(element);

            if (
              options.shouldNotDismissOnSelection &&
              !item.overrideNonDismissRestriction
            ) {
              menuItems = renderMenuOptions(menu, items());
            }
          }

          if (
            !options.shouldNotDismissOnSelection ||
            item.overrideNonDismissRestriction === true
          ) {
            resolve(item);
          }
        };
        element.addEventListener('click', handler);

        return () => element.removeEventListener('click', handler);
      });

    if (!options.backgroundIsNotDismissable) {
      container.addEventListener('click', containerClickListener);
      document.addEventListener('keydown', escKeyPressListener);
    }
  });
}
